import { Fragment, useEffect, useMemo, useState } from "react";

import { api } from "../../api.js";
import { navigateOrOpenNewTab } from "../../lib/dashboardNav.js";
import { formatEventTimeLocal } from "../../lib/dashboardTime.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import {
  colorForInteractionSessionId,
  InteractionSessionIdTailCell,
} from "../../lib/interactionSessionIdDisplay.js";
import { buildRawEventsUrl } from "../../lib/rawEventsUrl.js";
import type {
  FilterDevice,
  FilterState,
  FilterVenue,
  OpenRawEventsHandler,
  PaidSessionExplorerData,
  PaidSessionExplorerRow,
} from "../../types.js";
import { ChartCard, InfoPopover } from "../ChartCard.js";

function deviceDisplayName(
  row: PaidSessionExplorerRow,
  deviceLabelById: Map<string, string>,
): string {
  const fromFilters = deviceLabelById.get(row.deviceId);
  if (fromFilters) return fromFilters;
  const name = row.deviceName.trim();
  if (name) return name;
  return "—";
}

function venueDisplayName(
  row: PaidSessionExplorerRow,
  venueLabelById: Map<string, string>,
): string {
  const fromFilters = row.venueId ? venueLabelById.get(row.venueId) : undefined;
  if (fromFilters) return fromFilters;
  return row.venueName.trim();
}

function pageNumbersForPager(current: number, total: number): number[] {
  if (total <= 1) return [1];
  if (total <= 14) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const want = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  for (let p = 1; p <= 3; p++) want.add(p);
  for (let p = total - 2; p <= total; p++) want.add(p);
  return [...want].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
}

function paidSessionPropsFilter(paidSessionId: string): string {
  return `"paid_session_id":"${paidSessionId}"`;
}

const PAID_SESSION_EXPLORER_CRITERIA = `One row per distinct play session id (paid_session_id / mobile interactive_session_id) with payment_confirmed and/or shell-gap confirm-only rows in scope (date + venue + device filters only; Game filter ignored).

Registered rows: payment_succeeded exists for that play session id.
Shell gap rows (dark green): payment_confirmed with no table payment_succeeded matching by play session id or device + −2 min…+10 min — table missed unlock telemetry. ISID from linked events on that psid, else any table event on the device in that window (prefer payment_succeeded). Amount is in Gross Rev.; flagged as shell gap.

first_ts / last_ts: play_sessions.starts_at / ends_at when linked, else min / max client_timestamp on matching events.
amount_pence: prefer play_session_payments when linked; else confirmed / succeeded event amounts.

Row click: Registered → Raw Events → payment_succeeded for that paid_session_id; Shell gap → payment_confirmed.`;

const UNREGISTERED_AMOUNT_COLOR = "#15803d";

function ExplorerColumnHeader({
  label,
  info,
  showInfo,
}: {
  label: string;
  info: string;
  showInfo: boolean;
}) {
  return (
    <th className="px-2 py-2">
      {showInfo ? (
        <span className="inline-flex items-center gap-0.5 normal-case tracking-normal">
          {label}
          <InfoPopover text={info} label={`${label} column`} />
        </span>
      ) : (
        <span className="normal-case tracking-normal">{label}</span>
      )}
    </th>
  );
}

interface PaidSessionExplorerCardProps {
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  showCriteria?: boolean;
  showColumnInfo?: boolean;
  /** Fixed page size (e.g. Overview PDF snapshot). When set with hidePagination, loads one batch only. */
  pageSize?: number;
  hidePagination?: boolean;
}

export function PaidSessionExplorerCard({
  filters,
  reportingTimezone,
  onOpenRawEvents,
  showCriteria = true,
  showColumnInfo = true,
  pageSize,
  hidePagination = false,
}: PaidSessionExplorerCardProps) {
  const [listLimit, setListLimit] = useState(pageSize ?? 10);
  const [listOffset, setListOffset] = useState(0);
  const [data, setData] = useState<PaidSessionExplorerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<FilterDevice[]>([]);
  const [venues, setVenues] = useState<FilterVenue[]>([]);

  useEffect(() => {
    if (pageSize != null) setListLimit(pageSize);
  }, [pageSize]);

  useEffect(() => {
    setListOffset(0);
  }, [filters.from, filters.to, filters.venueId, filters.deviceId, filters.gameUrl, filters.timeframe]);

  const effectiveLimit = pageSize ?? listLimit;
  const effectiveOffset = hidePagination ? 0 : listOffset;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .retentionPaidSessions(filters, { limit: effectiveLimit, offset: effectiveOffset })
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load paid sessions");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters, effectiveLimit, effectiveOffset]);

  useEffect(() => {
    api
      .filters()
      .then((opts) => {
        setDevices(opts.devices ?? []);
        setVenues(opts.venues ?? []);
      })
      .catch(() => {
        /* labels optional */
      });
  }, []);

  const deviceLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of devices) {
      m.set(d.id, (d.name && d.name.trim()) || d.id);
    }
    return m;
  }, [devices]);
  const venueLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of venues) {
      m.set(v.id, (v.name && v.name.trim()) || v.id);
    }
    return m;
  }, [venues]);

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const listPage = Math.floor(effectiveOffset / effectiveLimit) + 1;
  const listTotalPages = Math.max(1, Math.ceil(total / effectiveLimit));
  const listHasMore = effectiveOffset + effectiveLimit < total;
  const pageNums = pageNumbersForPager(listPage, listTotalPages);

  const openPaidSession = (
    paidSessionId: string,
    registered: boolean,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey"> | null,
  ) => {
    const propsFilter = paidSessionPropsFilter(paidSessionId);
    const eventName = registered ? "payment_succeeded" : "payment_confirmed";
    const rawEventsUrl = buildRawEventsUrl({
      filters,
      eventName,
      propertiesContains: propsFilter,
    });
    if (navigateOrOpenNewTab(rawEventsUrl, e) === "new") return;
    onOpenRawEvents({
      eventName,
      propertiesContains: propsFilter,
    });
  };

  const openInteractionSession = (
    interactionSessionId: string,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey"> | null,
  ) => {
    const rawEventsUrl = buildRawEventsUrl({
      filters,
      eventName: "all",
      interactionSessionId,
    });
    if (navigateOrOpenNewTab(rawEventsUrl, e) === "new") return;
    onOpenRawEvents({
      eventName: "all",
      interactionSessionId,
    });
  };

  return (
    <ChartCard
      title="Paid session explorer"
      criteria={showCriteria ? PAID_SESSION_EXPLORER_CRITERIA : undefined}
      note={
        loading
          ? "Loading…"
          : error
            ? error
            : hidePagination && total > effectiveLimit
              ? `${total.toLocaleString()} paid session${total === 1 ? "" : "s"} · showing newest ${effectiveLimit}`
              : `${total.toLocaleString()} paid session${total === 1 ? "" : "s"} · newest first`
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left text-[12px] text-text-primary">
          <thead>
            <tr className="border-b border-border-subtle text-[10px] uppercase tracking-wide text-text-secondary">
              <ExplorerColumnHeader
                label="First (local)"
                info="Earliest client_timestamp for this paid session in scope, in the reporting timezone."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="Last (local)"
                info="Latest client_timestamp for this paid session in scope, in the reporting timezone."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="Amount"
                info="Prefer play_session_payments when linked; else confirmed event amount_pence (GBP). Dark green + Shell gap badge = confirmed with no table payment_succeeded on same play session id."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="Status"
                info="Registered = table payment_succeeded on same play session id. Shell gap = payment_confirmed only (shell telemetry missed)."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="Device"
                info="Device that emitted events for this paid session (from device_events.device_id)."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="Venue"
                info="Historical venue from play_sessions when linked; otherwise the device's current venue."
                showInfo={showColumnInfo}
              />
              <ExplorerColumnHeader
                label="ISID"
                info="interaction_session_id from linked events on same play session id (prefer payment_succeeded), else any table event on the device within −2 min…+10 min of confirm/succeed time. Confirms that match a table unlock by device+time appear on the table psid row, not as shell gaps. Colour matches Raw Events. Click: Raw Events → that stint."
                showInfo={showColumnInfo}
              />
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-text-secondary">
                  Loading paid sessions…
                </td>
              </tr>
            ) : error && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-text-secondary">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-text-secondary">
                  No payment_confirmed or shell-gap rows with play session id in
                  the current filter.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const venueName = venueDisplayName(row, venueLabelById);
                const isid = row.interactionSessionId.trim() || null;
                const isidColor = isid ? colorForInteractionSessionId(isid) : undefined;
                const registered = row.registered !== false;
                const eventName = registered ? "payment_succeeded" : "payment_confirmed";
                const rawEventsUrl = buildRawEventsUrl({
                  filters,
                  eventName,
                  propertiesContains: paidSessionPropsFilter(row.paidSessionId),
                });
                return (
                  <tr
                    key={row.paidSessionId}
                    className="cursor-pointer border-b border-border-subtle/60 hover:bg-[#1a2d4d]"
                    title={
                      registered
                        ? "Click: Raw Events → payment_succeeded for this paid_session_id"
                        : "Shell gap — payment_confirmed with no payment_succeeded on same id. Click: Raw Events → payment_confirmed for this paid_session_id"
                    }
                    onClick={(e) => openPaidSession(row.paidSessionId, registered, e)}
                    onAuxClick={(e) => {
                      if (e.button !== 1) return;
                      e.preventDefault();
                      navigateOrOpenNewTab(rawEventsUrl, e);
                    }}
                  >
                    <td className="px-2 py-2 text-[11px] whitespace-nowrap">
                      {formatEventTimeLocal(row.firstTs, reportingTimezone)}
                    </td>
                    <td className="px-2 py-2 text-[11px] whitespace-nowrap">
                      {formatEventTimeLocal(row.lastTs, reportingTimezone)}
                    </td>
                    <td
                      className="px-2 py-2 tabular-nums"
                      style={registered ? undefined : { color: UNREGISTERED_AMOUNT_COLOR }}
                    >
                      {formatGbpPence(row.amountPence)}
                    </td>
                    <td className="px-2 py-2">
                      {registered ? (
                        <span className="text-text-secondary">Registered</span>
                      ) : (
                        <span style={{ color: UNREGISTERED_AMOUNT_COLOR }} title="Paid on mobile; table never emitted payment_succeeded">
                          Shell gap
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">{deviceDisplayName(row, deviceLabelById)}</td>
                    <td
                      className="px-2 py-2"
                      title={
                        row.venueSource === "play_session"
                          ? "Venue from play_sessions.venue_id (historical, frozen at session creation)"
                          : "Venue from devices.venue_id today — may differ if the device moved since this payment"
                      }
                    >
                      {venueName || "—"}
                    </td>
                    <td className="px-2 py-2">
                      <InteractionSessionIdTailCell
                        id={isid}
                        accentColor={isidColor}
                        onClick={(id, e) => openInteractionSession(id, e)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {total > 0 && !hidePagination ? (
        <div className="no-print mt-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border-subtle pt-1.5 text-[11px] text-text-secondary">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setListOffset((o) => Math.max(0, o - effectiveLimit))}
              disabled={effectiveOffset <= 0 || loading}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setListOffset((o) => o + effectiveLimit)}
              disabled={!listHasMore || loading}
            >
              Next
            </button>
            <span className="tabular-nums">
              Page {listPage} of {listTotalPages}
            </span>
            <label className="inline-flex items-center gap-1">
              <span className="text-text-secondary">Rows/page</span>
              <select
                className="rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary"
                value={effectiveLimit}
                disabled={pageSize != null}
                onChange={(e) => {
                  setListLimit(Number.parseInt(e.target.value, 10));
                  setListOffset(0);
                }}
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {total > effectiveLimit ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex max-w-[min(100vw-24px,520px)] flex-wrap items-center gap-1 overflow-x-auto py-0.5">
                {pageNums.map((p, i) => (
                  <Fragment key={p}>
                    {i > 0 && pageNums[i - 1] !== undefined && p > pageNums[i - 1]! + 1 ? (
                      <span className="px-0.5 text-text-secondary" aria-hidden>
                        …
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className={`min-w-[1.75rem] rounded border px-1.5 py-0.5 tabular-nums ${
                        p === listPage
                          ? "border-accent bg-accent/20 text-text-primary"
                          : "border-border-subtle bg-panel-2 text-text-secondary hover:text-text-primary"
                      }`}
                      onClick={() => setListOffset((p - 1) * effectiveLimit)}
                      disabled={p === listPage || loading}
                    >
                      {p}
                    </button>
                  </Fragment>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </ChartCard>
  );
}
